import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { MessageSquare } from "lucide-react";
import api from "@/lib/api";
import { UserAvatar } from "@/components/UserAvatar";
import { fullName, timeAgo } from "@/lib/format";

export default function Messages() {
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/messages/threads");
      setThreads(data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, [load]);

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight">Messages</h1>
          <p className="text-sm text-muted-foreground mt-1">Private conversations with fellow Cornellians.</p>
        </div>
        <div className="w-12 h-12 rounded-2xl bg-[#B31B1B]/10 text-[#B31B1B] grid place-items-center shrink-0">
          <MessageSquare className="w-6 h-6" />
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12 text-muted-foreground" data-testid="messages-loading">Loading…</div>
      ) : threads.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground bg-white rounded-2xl border border-border px-6" data-testid="messages-empty">
          <MessageSquare className="w-8 h-8 mx-auto mb-3 text-[#B31B1B]/60" />
          <div className="font-semibold text-neutral-800">No messages yet</div>
          <div className="text-sm mt-1">Visit the <Link to="/directory" className="text-[#B31B1B] font-medium hover:underline">directory</Link> to find members and start a private conversation.</div>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-border cornell-shadow overflow-hidden divide-y divide-border" data-testid="messages-thread-list">
          {threads.map((t) => (
            <Link
              key={t.other_id}
              to={`/messages/${t.other_id}`}
              className="flex items-start gap-3 p-4 hover:bg-neutral-50 transition-colors"
              data-testid={`thread-${t.other_id}`}
            >
              <UserAvatar user={t.other_user} size={48} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <div className="font-semibold truncate flex-1">{fullName(t.other_user)}</div>
                  <div className="text-[11px] text-muted-foreground shrink-0">{timeAgo(t.last_message_at)}</div>
                </div>
                <div className="text-sm text-neutral-600 truncate mt-0.5">
                  {t.last_from_me && <span className="text-neutral-400">You: </span>}
                  {t.last_message}
                </div>
              </div>
              {t.unread_count > 0 && (
                <div
                  className="ml-1 min-w-[22px] h-[22px] rounded-full bg-[#B31B1B] text-white text-[11px] font-bold grid place-items-center px-1.5"
                  data-testid={`thread-unread-${t.other_id}`}
                >
                  {t.unread_count}
                </div>
              )}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
